import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Aviso } from '../componentes/piezas.js';
import { EscanerQr } from '../componentes/camara.js';
import { fijarTokenMovil } from '../servicios/api.js';
import { NuevaSolicitud } from './solicitudes.js';

/**
 * Reporte desde una tablet emparejada a una cuenta de sede (`/movil`). Sin sesión de usuario:
 * la identidad sale del token que quedó al emparejar. Vive fuera de la compuerta de login, igual
 * que `/s`, porque la tablet se empareja antes de tener sesión.
 *
 * El token se guarda en `localStorage` del origen de la app. Es el único sitio donde la app lo
 * guarda; al desvincular se borra. Ver `docs/emparejamiento-dispositivos-sede.md`.
 */
const CLAVE_TOKEN = 'continnua.dispositivoSede';

function leerToken(): string | null {
  try {
    return localStorage.getItem(CLAVE_TOKEN);
  } catch {
    return null;
  }
}

function guardarToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(CLAVE_TOKEN, token);
    else localStorage.removeItem(CLAVE_TOKEN);
  } catch {
    // Sin almacenamiento disponible la tablet no conserva el emparejamiento; se verá al abrir de nuevo.
  }
}

const CLAVE_TABLET = 'continnua.tabletId';

/** Identificador de esta tablet: se crea una vez y se conserva al desvincular, para agrupar su historial. */
function obtenerTabletId(): string {
  try {
    const existente = localStorage.getItem(CLAVE_TABLET);
    if (existente) return existente;
    const nuevo = crypto.randomUUID();
    localStorage.setItem(CLAVE_TABLET, nuevo);
    return nuevo;
  } catch {
    return crypto.randomUUID();
  }
}

async function llamarMovil<T>(
  ruta: string,
  { metodo, cuerpo, token }: { metodo: 'POST' | 'DELETE'; cuerpo?: unknown; token?: string },
): Promise<T> {
  const respuesta = await fetch(`/api/movil${ruta}`, {
    method: metodo,
    headers: {
      ...(cuerpo ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: cuerpo ? JSON.stringify(cuerpo) : undefined,
  });
  const datos = (await respuesta.json().catch(() => ({}))) as { mensaje?: string | string[] } & T;
  if (!respuesta.ok) {
    const mensaje = Array.isArray(datos.mensaje) ? datos.mensaje.join(' ') : datos.mensaje;
    throw new Error(mensaje ?? 'No se pudo completar la operación.');
  }
  return datos;
}

/**
 * Medidas de la pantalla de la tablet: la barra de estado queda por encima del contenido y los
 * bordes respetan las esquinas y la barra de gestos.
 */
const RAIZ_MOVIL: CSSProperties = {
  minHeight: '100dvh',
  boxSizing: 'border-box',
  paddingTop: 'max(36px, env(safe-area-inset-top))',
  paddingRight: 'max(16px, env(safe-area-inset-right))',
  paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
  paddingLeft: 'max(16px, env(safe-area-inset-left))',
};

export function MovilDispositivo(): JSX.Element {
  const [token, setToken] = useState<string | null>(leerToken);

  return (
    <main className="solicitud-publica-raiz" style={RAIZ_MOVIL}>
      {token ? (
        <Reportar
          token={token}
          alDesvincular={() => {
            guardarToken(null);
            setToken(null);
          }}
        />
      ) : (
        <Emparejar
          alEmparejar={(nuevoToken) => {
            guardarToken(nuevoToken);
            setToken(nuevoToken);
          }}
        />
      )}
    </main>
  );
}

function Emparejar({ alEmparejar }: { alEmparejar: (token: string) => void }): JSX.Element {
  const [codigo, setCodigo] = useState<string | null>(null);
  // Al abrir la tablet sin emparejar, el escáner ya está abierto: lo primero es escanear el QR.
  const [escaneando, setEscaneando] = useState(true);
  const [nombreDispositivo, setNombreDispositivo] = useState('');
  const [nombrePersona, setNombrePersona] = useState('');
  const [apellidosPersona, setApellidosPersona] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!codigo) return;
    setEnviando(true);
    setError(null);
    try {
      const { token } = await llamarMovil<{ token: string }>('/emparejar', {
        metodo: 'POST',
        cuerpo: {
          codigo,
          nombreDispositivo: nombreDispositivo.trim(),
          nombrePersona: nombrePersona.trim(),
          apellidosPersona: apellidosPersona.trim(),
          tabletId: obtenerTabletId(),
        },
      });
      alEmparejar(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo emparejar la tablet.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Emparejar tablet</h1>
        </div>
      </div>
      {error ? <Aviso tono="atencion">{error}</Aviso> : null}
      <div className="panel">
        <div className="panel-cabecera">
          <h2>1. Escanee el QR de la sede</h2>
        </div>
        <div className="panel-cuerpo">
          <button type="button" className="boton secundario" onClick={() => setEscaneando(true)}>
            {codigo ? 'Escanear otro QR' : 'Escanear QR de emparejamiento'}
          </button>
          {codigo ? <p className="campo-nota">Código leído. Complete los datos para emparejar.</p> : null}
        </div>
      </div>
      <form onSubmit={enviar}>
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>2. Datos de la tablet</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="campo">
              <label htmlFor="nombre-dispositivo">Nombre de la tablet</label>
              <input
                id="nombre-dispositivo"
                value={nombreDispositivo}
                onChange={(e) => setNombreDispositivo(e.target.value)}
                required
                maxLength={120}
                placeholder="Ej.: Tablet 1"
              />
            </div>
            <div className="campo" style={{ marginBottom: 0 }}>
              <label htmlFor="nombre-persona">Quién la usa</label>
              <input
                id="nombre-persona"
                value={nombrePersona}
                onChange={(e) => setNombrePersona(e.target.value)}
                required
                maxLength={200}
                placeholder="Ingresa tu nombre aquí"
              />
              <span className="campo-nota">Se escribe una sola vez. Las solicitudes salen a su nombre.</span>
            </div>
            <div className="campo" style={{ marginBottom: 0 }}>
              <label htmlFor="apellidos-persona">Apellidos</label>
              <input
                id="apellidos-persona"
                value={apellidosPersona}
                onChange={(e) => setApellidosPersona(e.target.value)}
                required
                maxLength={200}
                placeholder="Apellidos de quien la usa"
              />
            </div>
          </div>
        </div>
        <div className="acciones-formulario" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="submit" className="boton" disabled={!codigo || enviando || !nombreDispositivo.trim() || !nombrePersona.trim() || !apellidosPersona.trim()}>
            {enviando ? 'Emparejando…' : 'Emparejar'}
          </button>
        </div>
      </form>

      {escaneando ? (
        <EscanerQr
          alDetectar={(detectado) => {
            setEscaneando(false);
            setCodigo(detectado.trim());
          }}
          alCerrar={() => setEscaneando(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Pantalla de reporte: el mismo formulario de «Nueva solicitud» de la web, con las rutas de la tablet.
 * Desvincular está en la barra superior y pide confirmación.
 */
function Reportar({ token, alDesvincular }: { token: string; alDesvincular: () => void }): JSX.Element {
  // Antes de pintar el formulario: sus consultas a /movil necesitan el token ya fijado.
  fijarTokenMovil(token);
  const [version, setVersion] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [desvinculando, setDesvinculando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fijarTokenMovil(token);
    return () => fijarTokenMovil(null);
  }, [token]);

  async function desvincular() {
    setDesvinculando(true);
    setError(null);
    try {
      await llamarMovil('/emparejamiento', { metodo: 'DELETE', token });
    } catch {
      // Aunque el servidor no responda, la tablet se desvincula aquí: el token ya no se usa.
    } finally {
      setDesvinculando(false);
      alDesvincular();
    }
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <strong style={{ fontSize: 15 }}>Continnua</strong>
        {confirmando ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span className="campo-nota">¿Desvincular la tablet?</span>
            <button type="button" className="boton pequeno" onClick={desvincular} disabled={desvinculando}>
              {desvinculando ? 'Desvinculando…' : 'Sí'}
            </button>
            <button type="button" className="boton secundario pequeno" onClick={() => setConfirmando(false)}>
              No
            </button>
          </div>
        ) : (
          <button type="button" className="boton secundario pequeno" onClick={() => setConfirmando(true)}>
            Desvincular
          </button>
        )}
      </div>
      {error ? <Aviso tono="atencion">{error}</Aviso> : null}
      {aviso ? <Aviso tono="exito">{aviso}</Aviso> : null}
      <NuevaSolicitud
        key={version}
        publico={{
          codigo: '',
          tenant: null,
          inicial: { ubicacionId: null, activoId: null },
          movil: true,
          alCrear: (numero) => {
            setAviso(`Solicitud #${numero} registrada.`);
            // Formulario nuevo para la siguiente solicitud.
            setVersion((v) => v + 1);
          },
        }}
      />
    </>
  );
}
